import React, { useState, useRef, useCallback, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { extractTextFromFile } from '../../../services/ocr/OCRService';
import { gastosService, centrosCosteService, reglasClasificacionService, facturasService, gastosRecurrentesService } from '../../../services/financialService';
import { supabase } from '../../../lib/supabase';

const BUCKET = 'facturas-compras';

const CATEGORIAS_GASTO = [
  'ALQUILER','ELECTRICIDAD','AGUA','GESTORIA','SEGUROS','TELEFONIA_INTERNET',
  'SOFTWARE','SEGURIDAD_SOCIAL','PERSONAL','COMISIONES_BANCARIAS','TRANSPORTE',
  'COMBUSTIBLE','MANTENIMIENTO','REPARACIONES','PUBLICIDAD','MATERIAL',
  'SERVICIOS_PROFESIONALES','IMPUESTOS','MERCANCIA','OTROS',
];

const CLASIFICACIONES = ['COMPRA_MERCANCIA', 'GASTO_OPERATIVO', 'ACTIVO_INVERSION', 'NO_CLASIFICADO'];

const CLASIFICACION_COLORS = {
  COMPRA_MERCANCIA: '#34D399',
  GASTO_OPERATIVO: '#FBBF24',
  ACTIVO_INVERSION: '#A78BFA',
  NO_CLASIFICADO: 'rgba(255,255,255,0.30)',
};

// Deterministic classification based on keywords
const classifyDocument = (texto, proveedor) => {
  const t = (texto || '')?.toLowerCase();
  const p = (proveedor || '')?.toLowerCase();

  // Operational expense keywords
  const gastoKeywords = ['electricidad', 'luz', 'kwh', 'potencia', 'endesa', 'iberdrola', 'naturgy',
    'agua', 'm3', 'suministro', 'gestoría', 'gestoria', 'asesoría', 'asesoria', 'seguro', 'prima',
    'alquiler', 'arrendamiento', 'telefonía', 'telefonia', 'internet', 'fibra', 'movistar', 'vodafone',
    'mantenimiento', 'reparación', 'reparacion', 'publicidad', 'combustible', 'gasoil', 'gasolina',
    'software', 'licencia', 'suscripción', 'suscripcion', 'servicio profesional', 'honorarios'];

  const mercanciaKeywords = ['jamón', 'jamon', 'embutido', 'queso', 'producto', 'mercancía', 'mercancia',
    'alimento', 'conserva', 'bebida', 'carne', 'pescado', 'fruta', 'verdura'];

  const activoKeywords = ['maquinaria', 'equipo', 'instalación', 'instalacion', 'vehículo', 'vehiculo',
    'mobiliario', 'ordenador', 'servidor', 'reforma', 'obra'];

  const combined = t + ' ' + p;

  if (gastoKeywords?.some(kw => combined?.includes(kw))) return 'GASTO_OPERATIVO';
  if (mercanciaKeywords?.some(kw => combined?.includes(kw))) return 'COMPRA_MERCANCIA';
  if (activoKeywords?.some(kw => combined?.includes(kw))) return 'ACTIVO_INVERSION';
  return 'NO_CLASIFICADO';
};

// Suggest category from text
const suggestCategory = (texto, proveedor) => {
  const t = (texto || '')?.toLowerCase();
  const p = (proveedor || '')?.toLowerCase();
  const combined = t + ' ' + p;

  const rules = [
    { keywords: ['electricidad', 'kwh', 'endesa', 'iberdrola', 'naturgy', 'luz eléctrica'], cat: 'ELECTRICIDAD' },
    { keywords: ['agua', 'm3', 'canal', 'suministro agua'], cat: 'AGUA' },
    { keywords: ['gestoría', 'gestoria', 'asesoría', 'asesoria', 'contabilidad', 'fiscal'], cat: 'GESTORIA' },
    { keywords: ['seguro', 'prima', 'mapfre', 'axa', 'allianz', 'zurich'], cat: 'SEGUROS' },
    { keywords: ['alquiler', 'arrendamiento', 'renta mensual'], cat: 'ALQUILER' },
    { keywords: ['teléfono', 'telefono', 'internet', 'fibra', 'movistar', 'vodafone', 'orange'], cat: 'TELEFONIA_INTERNET' },
    { keywords: ['software', 'licencia', 'suscripción', 'suscripcion', 'saas'], cat: 'SOFTWARE' },
    { keywords: ['mantenimiento', 'revisión', 'revision', 'preventivo'], cat: 'MANTENIMIENTO' },
    { keywords: ['reparación', 'reparacion', 'avería', 'averia', 'arreglo'], cat: 'REPARACIONES' },
    { keywords: ['publicidad', 'marketing', 'anuncio', 'campaña', 'campaña'], cat: 'PUBLICIDAD' },
    { keywords: ['combustible', 'gasoil', 'gasolina', 'carburante'], cat: 'COMBUSTIBLE' },
    { keywords: ['transporte', 'envío', 'envio', 'mensajería', 'mensajeria', 'correos'], cat: 'TRANSPORTE' },
    { keywords: ['material', 'papelería', 'papeleria', 'oficina', 'consumible'], cat: 'MATERIAL' },
    { keywords: ['honorarios', 'servicio profesional', 'consultoría', 'consultoria'], cat: 'SERVICIOS_PROFESIONALES' },
    { keywords: ['impuesto', 'tributo', 'tasa', 'hacienda', 'agencia tributaria'], cat: 'IMPUESTOS' },
    { keywords: ['nómina', 'nomina', 'salario', 'sueldo', 'trabajador'], cat: 'PERSONAL' },
    { keywords: ['seguridad social', 'ss', 'cotización', 'cotizacion'], cat: 'SEGURIDAD_SOCIAL' },
  ];

  for (const rule of rules) {
    if (rule?.keywords?.some(kw => combined?.includes(kw))) return rule?.cat;
  }
  return 'OTROS';
};

// Parse numeric value from text
const parseNumeric = (text) => {
  if (!text) return null;
  const cleaned = text?.replace(/[^\d,.-]/g, '')?.replace(',', '.');
  const val = parseFloat(cleaned);
  return isNaN(val) ? null : val;
};

const OCRGastosWizard = ({ onClose, onConfirmed }) => {
  const fileRef = useRef(null);
  const [step, setStep] = useState('upload'); // upload | processing | review | confirm | done
  const [file, setFile] = useState(null);
  const [ocrResult, setOcrResult] = useState(null);
  const [centros, setCentros] = useState([]);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    proveedor: '', proveedor_cif: '', numero_factura: '',
    fecha: '', fecha_vencimiento: '', concepto: '',
    base_imponible: '', iva_porcentaje: 21, total: '',
    forma_pago: '', iban: '',
    clasificacion: 'NO_CLASIFICADO',
    categoria: 'OTROS', tipo: 'VARIABLE',
    centro_coste_id: '',
    periodicidad: '',
  });
  const [recurrenteMatch, setRecurrenteMatch] = useState(null);

  const loadCentros = useCallback(async () => {
    try {
      const data = await centrosCosteService?.getAll();
      setCentros(data);
    } catch (_) {}
  }, []);

  React.useEffect(() => { loadCentros(); }, [loadCentros]);

  const handleFileSelect = (f) => {
    if (!f) return;
    const allowed = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowed?.includes(f?.type)) {
      setError('Formato no soportado. Usa PDF, JPG, PNG o WEBP.');
      return;
    }
    setFile(f);
    setError(null);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    const f = e?.dataTransfer?.files?.[0];
    if (f) handleFileSelect(f);
  };

  const processOCR = async () => {
    if (!file) return;
    setStep('processing');
    setError(null);

    try {
      // Upload to storage first (always save the document)
      let documentUrl = null;
      try {
        const ext = file?.name?.split('.')?.pop();
        const path = `gastos/${Date.now()}_${file?.name}`;
        const { data: uploadData } = await supabase?.storage?.from(BUCKET)?.upload(path, file, { upsert: true });
        if (uploadData?.path) {
          const { data: urlData } = supabase?.storage?.from(BUCKET)?.getPublicUrl(uploadData?.path);
          documentUrl = urlData?.publicUrl;
        }
      } catch (_) {}

      // Extract text via OCR
      let textoExtraido = '';
      let ocrData = {};
      try {
        const result = await extractTextFromFile(file);
        textoExtraido = result?.text || result?.rawText || '';
        ocrData = result || {};
      } catch (ocrErr) {
        // OCR failed but we still have the document saved
        textoExtraido = '';
      }

      // Parse structured data from OCR text
      const lines = textoExtraido?.split('\n')?.filter(l => l?.trim());

      // Extract proveedor (first meaningful line)
      const proveedorLine = lines?.find(l => l?.length > 3 && l?.length < 60 && !/^\d/?.test(l));
      const proveedorText = ocrData?.proveedor_texto || proveedorLine || '';

      // Extract CIF
      const cifMatch = textoExtraido?.match(/[A-Z]\d{7}[A-Z0-9]|\d{8}[A-Z]/i);
      const cifText = ocrData?.cif_texto || cifMatch?.[0] || '';

      // Extract invoice number
      const numMatch = textoExtraido?.match(/(?:factura|fra|nº|num|número)[:\s#]*([A-Z0-9/-]+)/i);
      const numText = ocrData?.numero_factura || numMatch?.[1] || '';

      // Extract date
      const dateMatch = textoExtraido?.match(/(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{2,4})/);
      let dateText = '';
      if (dateMatch) {
        const [, d, m, y] = dateMatch;
        const year = y?.length === 2 ? `20${y}` : y;
        dateText = `${year}-${m?.padStart(2, '0')}-${d?.padStart(2, '0')}`;
      }

      // Extract totals
      const totalMatch = textoExtraido?.match(/total[:\s]*([0-9.,]+)\s*€?/i);
      const baseMatch = textoExtraido?.match(/base[:\s]*([0-9.,]+)\s*€?/i);
      const ivaMatch = textoExtraido?.match(/iva[:\s]*([0-9.,]+)\s*%?/i);

      const totalVal = parseNumeric(totalMatch?.[1]);
      const baseVal = parseNumeric(baseMatch?.[1]);
      const ivaVal = parseNumeric(ivaMatch?.[1]);

      // Classify document
      const clasificacion = classifyDocument(textoExtraido, proveedorText);
      const categoria = suggestCategory(textoExtraido, proveedorText);

      // Check classification rules
      let reglaMatch = null;
      try {
        reglaMatch = await reglasClasificacionService?.matchProveedor(proveedorText, cifText);
      } catch (_) {}

      // Check if matches a recurring expense (for OCR substitution circuit)
      let recMatch = null;
      try {
        recMatch = await gastosRecurrentesService?.matchRecurrente(proveedorText, cifText, totalVal);
        if (recMatch) setRecurrenteMatch(recMatch);
      } catch (_) {}

      const confianza = textoExtraido?.length > 100 ? 75 : textoExtraido?.length > 50 ? 50 : 25;

      const result = {
        textoExtraido,
        documentUrl,
        proveedorText,
        cifText,
        numText,
        dateText,
        totalVal,
        baseVal,
        ivaVal,
        clasificacion,
        categoria: reglaMatch?.categoria || categoria,
        tipo: reglaMatch?.tipo || (clasificacion === 'GASTO_OPERATIVO' ? 'FIJO' : 'VARIABLE'),
        centro_coste_id: reglaMatch?.centro_coste_id || '',
        periodicidad: reglaMatch?.periodicidad || '',
        confianza,
        reglaMatch,
      };

      setOcrResult(result);
      setForm(prev => ({
        ...prev,
        proveedor: proveedorText,
        proveedor_cif: cifText,
        numero_factura: numText,
        fecha: dateText,
        base_imponible: baseVal ? String(baseVal) : '',
        iva_porcentaje: ivaVal && ivaVal <= 100 ? ivaVal : 21,
        total: totalVal ? String(totalVal) : '',
        clasificacion,
        categoria: reglaMatch?.categoria || categoria,
        tipo: reglaMatch?.tipo || (clasificacion === 'GASTO_OPERATIVO' ? 'FIJO' : 'VARIABLE'),
        centro_coste_id: reglaMatch?.centro_coste_id || '',
        periodicidad: reglaMatch?.periodicidad || '',
      }));

      setStep('review');
    } catch (err) {
      setError(err?.message || 'Error al procesar el documento');
      setStep('upload');
    }
  };

  const handleConfirm = async () => {
    setSaving(true);
    setError(null);

    try {
      // Check for duplicates
      const dupCheck = await gastosService?.checkDuplicate({
        proveedorCif: form?.proveedor_cif,
        numeroFactura: form?.numero_factura,
        proveedor: form?.proveedor,
        fecha: form?.fecha,
        total: parseFloat(form?.total) || 0,
      });

      if (dupCheck?.isDuplicate) {
        setDuplicateWarning(dupCheck);
        setSaving(false);
        return;
      }

      await saveDocument();
    } catch (err) {
      setError(err?.message || 'Error al guardar');
      setSaving(false);
    }
  };

  const saveDocument = async () => {
    setSaving(true);
    try {
      const baseImponible = parseFloat(form?.base_imponible) || 0;
      const ivaPct = parseFloat(form?.iva_porcentaje) || 21;

      if (form?.clasificacion === 'GASTO_OPERATIVO' || form?.clasificacion === 'NO_CLASIFICADO') {
        // Save as gasto
        const gasto = await gastosService?.create({
          fecha: form?.fecha || new Date()?.toISOString()?.split('T')?.[0],
          descripcion: form?.concepto || `Factura ${form?.proveedor || 'sin proveedor'}`,
          proveedor: form?.proveedor,
          proveedor_cif: form?.proveedor_cif,
          numero_factura: form?.numero_factura,
          categoria: form?.categoria,
          tipo: form?.tipo,
          centro_coste_id: form?.centro_coste_id || null,
          base_imponible: baseImponible,
          iva_porcentaje: ivaPct,
          estado: 'PENDIENTE',
          fecha_vencimiento: form?.fecha_vencimiento || null,
          forma_pago: form?.forma_pago || null,
          documento_url: ocrResult?.documentUrl,
          origen: 'OCR',
          es_prevision: false,
        });

        // If matches a recurring expense, substitute the preview
        if (recurrenteMatch && gasto?.id && form?.fecha) {
          try {
            const periodoKey = recurrenteMatch?.id + '_' + form?.fecha?.substring(0, 7);
            await gastosRecurrentesService?.sustituirPrevisionPorReal(recurrenteMatch?.id, periodoKey, gasto?.id);
          } catch (_) {}
        }

        // Save classification rule if proveedor known
        if (form?.proveedor && form?.categoria !== 'OTROS') {
          try {
            await reglasClasificacionService?.saveRule({
              proveedor_patron: form?.proveedor?.toLowerCase(),
              proveedor_cif: form?.proveedor_cif || null,
              categoria: form?.categoria,
              tipo: form?.tipo,
              centro_coste_id: form?.centro_coste_id || null,
              periodicidad: form?.periodicidad || null,
            });
          } catch (_) {}
        }

        if (ocrResult?.reglaMatch?.id) {
          try { await reglasClasificacionService?.incrementUsage(ocrResult?.reglaMatch?.id); } catch (_) {}
        }
      } else if (form?.clasificacion === 'COMPRA_MERCANCIA') {
        // Save as factura recibida
        await facturasService?.create({
          tipo: 'RECIBIDA',
          numero: form?.numero_factura,
          fecha: form?.fecha || new Date()?.toISOString()?.split('T')?.[0],
          fecha_vencimiento: form?.fecha_vencimiento || null,
          emisor: form?.proveedor,
          emisor_cif: form?.proveedor_cif,
          base_imponible: baseImponible,
          iva_importe: baseImponible * ivaPct / 100,
          total: parseFloat(form?.total) || baseImponible * (1 + ivaPct / 100),
          cobrado_pagado: 0,
          estado: 'PENDIENTE',
          centro_coste_id: form?.centro_coste_id || null,
          origen: 'OCR',
          documento_url: ocrResult?.documentUrl,
        });
      }

      setStep('done');
      if (onConfirmed) onConfirmed();
    } catch (err) {
      setError(err?.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full px-3 py-2 rounded-xl text-xs font-oranienbaum text-white/80 outline-none bg-transparent';
  const inputStyle = { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.80)' }}>
      <div className="w-full max-w-3xl rounded-2xl max-h-[92vh] overflow-y-auto" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.10)' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/8">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(214,239,78,0.10)', border: '1px solid rgba(214,239,78,0.25)' }}>
              <Icon name="ScanLine" size={18} className="text-[#D6EF4E]" />
            </div>
            <div>
              <h2 className="font-oranienbaum font-bold text-white text-lg">OCR Facturas y Gastos</h2>
              <p className="font-oranienbaum text-white/40 text-xs">Mercancía · Suministros · Servicios · Gastos operativos</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white/80 transition-colors"><Icon name="X" size={18} /></button>
        </div>

        {/* Steps indicator */}
        <div className="flex items-center gap-2 px-5 py-3 border-b border-white/5">
          {[
            { key: 'upload', label: 'Subir' },
            { key: 'processing', label: 'Procesando' },
            { key: 'review', label: 'Revisar' },
            { key: 'done', label: 'Listo' },
          ]?.map((s, i) => (
            <React.Fragment key={s?.key}>
              <div className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px] font-oranienbaum transition-colors ${step === s?.key ? 'text-black font-semibold' : 'text-white/30'}`}
                style={step === s?.key ? { background: '#D6EF4E' } : {}}>
                <span>{i + 1}</span>
                <span>{s?.label}</span>
              </div>
              {i < 3 && <div className="h-px flex-1" style={{ background: 'rgba(255,255,255,0.08)' }} />}
            </React.Fragment>
          ))}
        </div>

        <div className="p-5">
          {error && (
            <div className="mb-4 p-3 rounded-xl flex items-center gap-3" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>
              <Icon name="AlertCircle" size={15} className="text-red-400 flex-shrink-0" />
              <p className="font-oranienbaum text-red-400 text-xs">{error}</p>
              <button onClick={() => setError(null)} className="ml-auto text-red-400/60 hover:text-red-400"><Icon name="X" size={13} /></button>
            </div>
          )}

          {/* STEP: Upload */}
          {step === 'upload' && (
            <div>
              <div
                onDrop={handleDrop}
                onDragOver={e => e?.preventDefault()}
                onClick={() => fileRef?.current?.click()}
                className="border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-colors hover:border-[#D6EF4E]/40"
                style={{ borderColor: file ? 'rgba(214,239,78,0.40)' : 'rgba(255,255,255,0.12)', background: file ? 'rgba(214,239,78,0.04)' : 'rgba(255,255,255,0.02)' }}>
                <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden"
                  onChange={e => handleFileSelect(e?.target?.files?.[0])} />
                <Icon name={file ? 'FileCheck' : 'Upload'} size={32} className={file ? 'text-[#D6EF4E] mx-auto mb-3' : 'text-white/20 mx-auto mb-3'} />
                {file ? (
                  <>
                    <p className="font-oranienbaum text-[#D6EF4E] text-sm font-semibold">{file?.name}</p>
                    <p className="font-oranienbaum text-white/40 text-xs mt-1">{(file?.size / 1024)?.toFixed(0)} KB · Listo para procesar</p>
                  </>
                ) : (
                  <>
                    <p className="font-oranienbaum text-white/50 text-sm">Arrastra o haz clic para subir</p>
                    <p className="font-oranienbaum text-white/25 text-xs mt-1">PDF · JPG · PNG · WEBP · Foto de móvil</p>
                  </>
                )}
              </div>

              <div className="mt-4 p-3 rounded-xl" style={{ background: 'rgba(214,239,78,0.04)', border: '1px solid rgba(214,239,78,0.12)' }}>
                <p className="font-oranienbaum text-[#D6EF4E]/70 text-xs font-semibold mb-1">Este OCR acepta:</p>
                <div className="grid grid-cols-2 gap-1">
                  {['Facturas de mercancía', 'Recibos de electricidad', 'Facturas de agua/gas', 'Facturas de gestoría', 'Seguros', 'Software/SaaS', 'Telefonía/Internet', 'Mantenimiento y reparaciones', 'Publicidad', 'Transporte/Combustible', 'Servicios profesionales', 'Cualquier gasto operativo']?.map(item => (
                    <div key={item} className="flex items-center gap-1.5">
                      <Icon name="Check" size={10} className="text-[#D6EF4E]/60" />
                      <span className="font-oranienbaum text-white/40 text-[10px]">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end mt-4">
                <button onClick={processOCR} disabled={!file}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-oranienbaum font-semibold text-black disabled:opacity-40 transition-all hover:opacity-90"
                  style={{ background: '#D6EF4E' }}>
                  <Icon name="ScanLine" size={14} />
                  Procesar con OCR
                </button>
              </div>
            </div>
          )}

          {/* STEP: Processing */}
          {step === 'processing' && (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              <div className="w-12 h-12 rounded-full border-2 border-white/10 border-t-[#D6EF4E] animate-spin" />
              <p className="font-oranienbaum text-white/50 text-sm">Extrayendo texto del documento...</p>
              <p className="font-oranienbaum text-white/25 text-xs">OCR.Space · Clasificación automática · Detección de reglas</p>
            </div>
          )}

          {/* STEP: Review */}
          {step === 'review' && (
            <div>
              {/* Confidence & classification */}
              <div className="flex items-center gap-3 mb-4 p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ background: ocrResult?.confianza >= 70 ? '#34D399' : ocrResult?.confianza >= 40 ? '#FBBF24' : '#F87171' }} />
                  <span className="font-oranienbaum text-white/50 text-xs">Confianza OCR: <span className="font-semibold text-white/70">{ocrResult?.confianza || 0}%</span></span>
                </div>
                <div className="h-3 w-px bg-white/10" />
                <span className="font-oranienbaum text-white/50 text-xs">Clasificación sugerida:</span>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-oranienbaum font-semibold"
                  style={{ background: `${CLASIFICACION_COLORS?.[form?.clasificacion] || '#60A5FA'}15`, color: CLASIFICACION_COLORS?.[form?.clasificacion] || '#60A5FA', border: `1px solid ${CLASIFICACION_COLORS?.[form?.clasificacion] || '#60A5FA'}30` }}>
                  {form?.clasificacion}
                </span>
                {ocrResult?.reglaMatch && (
                  <span className="px-2 py-0.5 rounded-full text-[9px] font-oranienbaum" style={{ background: 'rgba(214,239,78,0.10)', border: '1px solid rgba(214,239,78,0.20)', color: '#D6EF4E' }}>
                    Regla aplicada
                  </span>
                )}
              </div>

              {/* Recurring expense match alert */}
              {recurrenteMatch && (
                <div className="mb-4 p-3 rounded-xl" style={{ background: 'rgba(214,239,78,0.06)', border: '1px solid rgba(214,239,78,0.20)' }}>
                  <div className="flex items-center gap-2 mb-1">
                    <Icon name="Repeat" size={13} className="text-[#D6EF4E]" />
                    <span className="font-oranienbaum text-[#D6EF4E] text-xs font-semibold">Posible gasto recurrente relacionado</span>
                  </div>
                  <p className="font-oranienbaum text-white/50 text-xs">
                    {recurrenteMatch?.nombre} · {recurrenteMatch?.periodicidad_pago || recurrenteMatch?.periodicidad} · {recurrenteMatch?.importe_habitual} €
                  </p>
                  <p className="font-oranienbaum text-white/30 text-[10px] mt-0.5">
                    Al confirmar, la previsión existente será sustituida por este gasto real. No se duplicará.
                  </p>
                </div>
              )}

              {/* Duplicate warning */}
              {duplicateWarning && (
                <div className="mb-4 p-3 rounded-xl" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.25)' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="AlertTriangle" size={14} className="text-[#F87171]" />
                    <span className="font-oranienbaum text-[#F87171] text-xs font-semibold">Posible duplicado detectado ({duplicateWarning?.reason})</span>
                  </div>
                  <p className="font-oranienbaum text-white/50 text-xs">Ya existe un registro similar: {duplicateWarning?.existing?.descripcion} · {duplicateWarning?.existing?.fecha}</p>
                  <div className="flex gap-2 mt-2">
                    <button onClick={() => setDuplicateWarning(null)} className="px-3 py-1.5 rounded-lg text-[10px] font-oranienbaum text-white/50" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }}>
                      Cancelar
                    </button>
                    <button onClick={saveDocument} className="px-3 py-1.5 rounded-lg text-[10px] font-oranienbaum font-semibold text-black" style={{ background: '#F87171' }}>
                      Guardar igualmente
                    </button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Clasificación */}
                <div className="md:col-span-2">
                  <label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Clasificación del Documento</label>
                  <div className="flex gap-2 flex-wrap">
                    {CLASIFICACIONES?.map(c => (
                      <button key={c} onClick={() => setForm(p => ({ ...p, clasificacion: c }))}
                        className={`px-3 py-1.5 rounded-lg text-[10px] font-oranienbaum transition-all ${form?.clasificacion === c ? 'font-semibold' : 'text-white/40 hover:text-white/70'}`}
                        style={form?.clasificacion === c
                          ? { background: `${CLASIFICACION_COLORS?.[c]}20`, border: `1px solid ${CLASIFICACION_COLORS?.[c]}40`, color: CLASIFICACION_COLORS?.[c] }
                          : { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                        {c?.replace(/_/g, ' ')}
                      </button>
                    ))}
                  </div>
                </div>

                {[
                  { label: 'Proveedor', key: 'proveedor', type: 'text' },
                  { label: 'CIF/NIF', key: 'proveedor_cif', type: 'text' },
                  { label: 'Nº Factura', key: 'numero_factura', type: 'text' },
                  { label: 'Fecha', key: 'fecha', type: 'date' },
                  { label: 'Fecha Vencimiento', key: 'fecha_vencimiento', type: 'date' },
                  { label: 'Concepto', key: 'concepto', type: 'text' },
                  { label: 'Base Imponible (€)', key: 'base_imponible', type: 'number' },
                  { label: 'IVA (%)', key: 'iva_porcentaje', type: 'number' },
                  { label: 'Total (€)', key: 'total', type: 'number' },
                  { label: 'Forma de Pago', key: 'forma_pago', type: 'text' },
                ]?.map(f => (
                  <div key={f?.key}>
                    <label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">{f?.label}</label>
                    <input type={f?.type} value={form?.[f?.key]} onChange={e => setForm(p => ({ ...p, [f?.key]: e?.target?.value }))}
                      className={inputCls} style={inputStyle} />
                  </div>
                ))}

                <div>
                  <label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Categoría</label>
                  <select value={form?.categoria} onChange={e => setForm(p => ({ ...p, categoria: e?.target?.value }))} className={inputCls} style={inputStyle}>
                    {CATEGORIAS_GASTO?.map(c => <option key={c} value={c} style={{ background: '#141414' }}>{c?.replace(/_/g, ' ')}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Tipo</label>
                  <select value={form?.tipo} onChange={e => setForm(p => ({ ...p, tipo: e?.target?.value }))} className={inputCls} style={inputStyle}>
                    <option value="FIJO" style={{ background: '#141414' }}>FIJO</option>
                    <option value="VARIABLE" style={{ background: '#141414' }}>VARIABLE</option>
                  </select>
                </div>

                <div>
                  <label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Centro de Coste</label>
                  <select value={form?.centro_coste_id} onChange={e => setForm(p => ({ ...p, centro_coste_id: e?.target?.value }))} className={inputCls} style={inputStyle}>
                    <option value="" style={{ background: '#141414' }}>— Sin asignar —</option>
                    {centros?.map(c => <option key={c?.id} value={c?.id} style={{ background: '#141414' }}>{c?.nombre}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Periodicidad</label>
                  <select value={form?.periodicidad} onChange={e => setForm(p => ({ ...p, periodicidad: e?.target?.value }))} className={inputCls} style={inputStyle}>
                    <option value="" style={{ background: '#141414' }}>— No recurrente —</option>
                    {['MENSUAL', 'TRIMESTRAL', 'SEMESTRAL', 'ANUAL']?.map(p => <option key={p} value={p} style={{ background: '#141414' }}>{p}</option>)}
                  </select>
                </div>
              </div>

              {/* OCR raw text */}
              {ocrResult?.textoExtraido && (
                <details className="mt-4">
                  <summary className="font-oranienbaum text-white/30 text-xs cursor-pointer hover:text-white/50 transition-colors">Ver texto extraído por OCR</summary>
                  <div className="mt-2 p-3 rounded-xl max-h-32 overflow-y-auto" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <pre className="font-mono text-white/30 text-[10px] whitespace-pre-wrap">{ocrResult?.textoExtraido}</pre>
                  </div>
                </details>
              )}

              <div className="flex justify-between mt-5">
                <button onClick={() => { setStep('upload'); setFile(null); setOcrResult(null); }}
                  className="px-4 py-2 rounded-xl text-xs font-oranienbaum text-white/50 hover:text-white/80 transition-colors"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  Volver
                </button>
                <button onClick={handleConfirm} disabled={saving}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-oranienbaum font-semibold text-black disabled:opacity-50 transition-all hover:opacity-90"
                  style={{ background: '#D6EF4E' }}>
                  {saving ? (
                    <><div className="w-3 h-3 rounded-full border border-black/30 border-t-black animate-spin" />Guardando...</>
                  ) : (
                    <><Icon name="Check" size={14} />Confirmar y Guardar</>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* STEP: Done */}
          {step === 'done' && (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.25)' }}>
                <Icon name="CheckCircle" size={32} className="text-[#34D399]" />
              </div>
              <p className="font-oranienbaum font-bold text-white text-lg">Documento guardado</p>
              <p className="font-oranienbaum text-white/40 text-sm text-center">
                {form?.clasificacion === 'COMPRA_MERCANCIA' ? 'Registrado como factura recibida' : 'Registrado como gasto operativo'}
                {form?.proveedor && ` · ${form?.proveedor}`}
              </p>
              {form?.proveedor && form?.categoria !== 'OTROS' && (
                <p className="font-oranienbaum text-[#D6EF4E]/60 text-xs">Regla de clasificación guardada para futuros documentos de este proveedor</p>
              )}
              <div className="flex gap-3 mt-2">
                <button onClick={() => { setStep('upload'); setFile(null); setOcrResult(null); setForm({ proveedor: '', proveedor_cif: '', numero_factura: '', fecha: '', fecha_vencimiento: '', concepto: '', base_imponible: '', iva_porcentaje: 21, total: '', forma_pago: '', iban: '', clasificacion: 'NO_CLASIFICADO', categoria: 'OTROS', tipo: 'VARIABLE', centro_coste_id: '', periodicidad: '' }); }}
                  className="px-4 py-2 rounded-xl text-xs font-oranienbaum font-semibold text-black" style={{ background: '#D6EF4E' }}>
                  Procesar otro documento
                </button>
                <button onClick={onClose} className="px-4 py-2 rounded-xl text-xs font-oranienbaum text-white/50" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  Cerrar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OCRGastosWizard;
